import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { ListPagination } from "@/components/shared/ListPagination";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { EditRequestDetailDialog } from "./EditRequestDetailDialog";
import {
  type EditRequestStatus,
  type EditRequestSummary,
  useApproveEditRequest,
  useEditRequestList,
} from "@/hooks/useBusinessEditRequests";
import { formatDate } from "@/utils/format";

type TabKey = "pending" | "history";

const tabs: Array<{ key: TabKey; label: string; status?: EditRequestStatus }> =
  [
    { key: "pending", label: "Pending", status: "PENDING" },
    { key: "history", label: "History", status: undefined },
  ];

const DEFAULT_PAGE_SIZE = 25;

export const EditRequestsList = () => {
  const [tab, setTab] = useState<TabKey>("pending");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [openRequest, setOpenRequest] = useState<EditRequestSummary | null>(
    null,
  );
  const statusFilter = tabs.find((t) => t.key === tab)?.status;

  const { data, isLoading, error, refetch } = useEditRequestList({
    status: statusFilter,
    page,
    limit,
  });
  const approve = useApproveEditRequest();

  const rows =
    tab === "history"
      ? (data?.data ?? []).filter((r) => r.status !== "PENDING")
      : (data?.data ?? []);
  const pagination = data?.pagination;

  const handleApprove = async (req: EditRequestSummary) => {
    try {
      await approve.mutateAsync({ id: req.id, businessId: req.businessId });
      toast.success("Edit request approved");
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const statusBadge = (status: EditRequestStatus) => {
    const cls =
      status === "PENDING"
        ? "bg-warning/10 text-warning"
        : status === "APPROVED"
          ? "bg-success/10 text-success"
          : status === "REJECTED"
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground";
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
        {status.toLowerCase()}
      </span>
    );
  };

  const columns: Array<DataTableColumn<EditRequestSummary>> = [
    {
      key: "business",
      header: "Business",
      render: (r) => (
        <>
          <Link
            to={`/businesses/${r.businessId}`}
            className="font-medium hover:underline"
          >
            {r.business.businessName}
          </Link>
          <div className="text-xs text-muted-foreground truncate max-w-xs">
            {r.business.nameEn} · {r.business.city}
          </div>
        </>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (r) => (
        <span className="text-muted-foreground">
          {r.business.owner?.name ?? "—"}
        </span>
      ),
    },
    {
      key: "fields",
      header: "Fields changed",
      render: (r) => (
        <span className="text-muted-foreground">
          {Object.keys(r.changes ?? {}).length}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (r) => statusBadge(r.status) },
    {
      key: "submitted",
      header: "Submitted",
      render: (r) => (
        <span className="text-muted-foreground">{formatDate(r.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div
          className="flex gap-2 justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          <Button size="sm" variant="outline" onClick={() => setOpenRequest(r)}>
            Review
          </Button>
          {r.status === "PENDING" ? (
            <PermissionGate module="businesses" action="approve">
              <Button
                size="sm"
                disabled={approve.isPending}
                onClick={() => handleApprove(r)}
              >
                Approve
              </Button>
            </PermissionGate>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Edit requests">
      <div className="flex gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={`px-3 py-1.5 text-sm rounded-btn border ${
              tab === t.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle={
          tab === "pending"
            ? "No pending edit requests"
            : "No edit request history"
        }
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpenRequest(r)}
        />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="requests"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>

      <EditRequestDetailDialog
        request={openRequest}
        onClose={() => setOpenRequest(null)}
        onReviewed={() => {
          setOpenRequest(null);
          refetch();
        }}
      />
    </AppLayout>
  );
};

export default EditRequestsList;
